import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { useState, useEffect } from 'react'

const NAV_LINKS = [
  { to: '/', label: 'Order' },
  { to: '/home', label: 'Home' },
  { to: '/our-story', label: 'Our Story' },
  { to: '/menu', label: 'Menu' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/careers', label: 'Careers' },
  { to: '/contact', label: 'Contact' },
]

export default function PublicLayout({ children }) {
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="grain-overlay min-h-screen bg-umber font-body text-cream">
      {/* Navbar — always white bg with bottom border */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[rgba(253,251,247,0.97)] backdrop-blur-xl border-b border-[#E5DDD4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.jpg" alt="Desres Kitchen" className="w-10 h-10 object-contain" />
            <span className="font-display text-[#E8761A] text-lg tracking-[0.08em]">DESRES KITCHEN</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`font-body text-[13px] tracking-[0.08em] uppercase transition-colors ${
                  location.pathname === link.to
                    ? 'text-terracotta'
                    : 'text-muted hover:text-cream'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/app"
              className="bg-terracotta text-white font-body text-[11px] font-bold tracking-[0.1em] uppercase px-5 py-2 rounded-full hover:bg-terracotta-glow transition-all"
            >
              Staff Portal
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-cream hover:text-terracotta transition-colors"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-umber border-t border-border-dark px-4 py-4 space-y-1">
            {NAV_LINKS.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`block font-body text-[13px] tracking-[0.08em] uppercase py-3 border-b border-border-dark transition-colors ${
                  location.pathname === link.to ? 'text-terracotta' : 'text-muted'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/app"
              onClick={() => setMobileOpen(false)}
              className="block bg-terracotta text-white font-body text-[11px] font-bold tracking-[0.1em] uppercase px-5 py-3 text-center mt-3 rounded-full hover:bg-terracotta-glow transition-all"
            >
              Staff Portal
            </Link>
          </div>
        )}
      </nav>

      <main className="pt-16">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-charcoal border-t border-border-dark mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.jpg" alt="Desres Kitchen" className="w-9 h-9 object-contain" />
                <span className="font-display text-cream tracking-[0.06em]">DESRES KITCHEN</span>
              </div>
              <p className="text-sm text-muted leading-relaxed">
                A family business for over 25 years. African cuisine, daily bakes, sweet treats, and customised cakes — serving Limpopo and Pretoria with love.
              </p>
            </div>
            <div>
              <h4 className="font-body text-[11px] tracking-[0.2em] uppercase text-terracotta mb-4 font-extrabold">Branches</h4>
              <div className="space-y-2 text-sm text-muted">
                <p>Desres Kitchen — Limpopo</p>
                <p>Yahweh's Delight — Pretoria</p>
              </div>
            </div>
            <div>
              <h4 className="font-body text-[11px] tracking-[0.2em] uppercase text-terracotta mb-4 font-extrabold">Quick Links</h4>
              <div className="space-y-2">
                {NAV_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="block text-sm text-muted hover:text-terracotta transition-colors">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="h-px mt-10 mb-6" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
          <p className="text-center text-[11px] text-muted tracking-[0.1em]">
            &copy; {new Date().getFullYear()} Desres Kitchen & Confectionery. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}
